import React, { useState } from 'react';
import { css } from '@emotion/css';
import ModalLayout from '../structure/ModalLayout';
import FooterBar from '../structure/FooterBar';
import Button from '../general/Button';
import ApplicationVariable from '../../models/applications/ApplicationVariable';
import CustomScrollbars from '../structure/CustomScrollbars';
import ApplicationVariablesContainer from '../../hooks/ApplicationVariablesContainer';
import ConfirmationDialog from '../structure/ConfirmationDialog';
import RemoveConfirmation from '../general/RemoveConfirmation';
import { color_shades_dark, color_shades_darker, color_shades_darkest, color_colors_ocean, color_colors_light_ocean } from '../../constants/colors';
import PageError from '../general/PageError';
import { getFieldVariationDisplayName } from '../../utils/fieldVariations';

const closeIcon = require('../../content/images/close_icon.svg');
const deleteIcon = require('../../content/images/content-item-icons/delete-blue.svg');

interface ViewFieldVariationsModalProps {
    fieldTitle: string;
    variations: ApplicationVariable[];
    isVisible: boolean;
    onClose: () => void;
    onSelectVariation: (variation: ApplicationVariable) => void;
}

const ViewFieldVariationsModal: React.FC<ViewFieldVariationsModalProps> = (props) => {
    const variablesContainer = ApplicationVariablesContainer.useContainer();
    const [selectedVariation, setSelectedVariation] = useState<ApplicationVariable | null>(null);
    const [isDeleteConfirmationOpen, setIsDeleteConfirmationOpen] = useState(false);
    const [variationToDelete, setVariationToDelete] = useState<ApplicationVariable | null>(null);

    const handleSelect = () => {
        if (selectedVariation) {
            props.onSelectVariation(selectedVariation);
            props.onClose();
        }
    };

    const handleDeleteVariation = (e: React.MouseEvent, variation: ApplicationVariable) => {
        e.preventDefault();
        e.stopPropagation();
        setVariationToDelete(variation);
        setIsDeleteConfirmationOpen(true);
    };

    const confirmDelete = async () => {
        if (variationToDelete?.id) {
            const result = await variablesContainer.deleteApplicationVariable(variationToDelete.id);
            if (result?.resultType === "Ok") {
                if (selectedVariation?.id === variationToDelete.id) {
                    setSelectedVariation(null);
                }
                setIsDeleteConfirmationOpen(false);
                setVariationToDelete(null);
            }
        }
    };

    const closeDeleteConfirmation = () => {
        setIsDeleteConfirmationOpen(false);
        setVariationToDelete(null);
    };

    const extractVariationName = (constantName: string): string => {
        return getFieldVariationDisplayName(constantName, props.fieldTitle);
    };

    return (
        <>
            <ModalLayout
                className={modalStyle}
                isVisible={props.isVisible}
                onClose={props.onClose}
                zIndex={900}
            >
                <div className={containerStyle}>
                    <div className="header-wrapper">
                        <h2>Saved Variations for {props.fieldTitle}</h2>
                        <Button
                            className="close-icon"
                            themes={['end-tight', 'icon']}
                            icon={closeIcon}
                            type="button"
                            onClick={props.onClose}
                        />
                    </div>

                    <div className="variations-container">
                        <CustomScrollbars autoHide>
                            <div className="scrollable-content">
                                {props.variations.length === 0 ? (
                                    <div className="empty-state">
                                        <p>No saved variations found for this field.</p>
                                    </div>
                                ) : (
                                    props.variations.map((variation) => (
                                        <div
                                            key={variation.id ?? variation.name}
                                            className={`variation-item ${selectedVariation?.id === variation.id ? 'selected' : ''}`}
                                            onClick={() => setSelectedVariation(variation)}
                                            onKeyDown={(event) => {
                                                if (event.key === 'Enter' || event.key === ' ') {
                                                    event.preventDefault();
                                                    setSelectedVariation(variation);
                                                }
                                            }}
                                            role="button"
                                            tabIndex={0}
                                        >
                                            <div className="variation-header">
                                                <div className="variation-name">{extractVariationName(variation.name)}</div>
                                                <div className="variation-actions">
                                                    <div className="variation-full-name">{variation.name}</div>
                                                    <Button
                                                        themes={['icon']}
                                                        icon={deleteIcon}
                                                        onClick={(e) => handleDeleteVariation(e, variation)}
                                                        className="delete-button"
                                                        type="button"
                                                    />
                                                </div>
                                            </div>
                                            <div className="variation-value">{variation.value}</div>
                                        </div>
                                    ))
                                )}
                            </div>
                        </CustomScrollbars>
                    </div>

                    <FooterBar>
                        <Button
                            themes={['primary', 'wide']}
                            onClick={handleSelect}
                            text="Use Selected Variation"
                            type="button"
                            disabled={!selectedVariation}
                        />
                        <Button
                            themes={['white', 'end']}
                            onClick={props.onClose}
                            text="Cancel"
                            type="button"
                        />
                    </FooterBar>
                </div>
            </ModalLayout>
            {isDeleteConfirmationOpen && (
                <ConfirmationDialog
                    title="Delete Variation"
                    deleteText="Delete"
                    onClose={closeDeleteConfirmation}
                    onConfirm={confirmDelete}
                >
                    <RemoveConfirmation
                        memberName={variationToDelete ? extractVariationName(variationToDelete.name) : ''}
                        removalTarget="saved variations"
                    />
                </ConfirmationDialog>
            )}
            <PageError errors={variablesContainer.errors} onClear={() => variablesContainer.setErrors([])} className={pageErrorStyle} />
        </>
    );
};

const pageErrorStyle = css`
    z-index: 2000;
`;

const modalStyle = css`
    width: 60vw;
    min-width: 600px;
    max-width: 900px;
    height: 70vh;
    max-height: 70vh;
    display: flex;
    flex-direction: column;

    @media (max-width: 640px) {
        width: calc(100vw - 12px);
        min-width: 0;
        max-width: calc(100vw - 12px);
        height: calc(100vh - 12px);
        height: calc(100dvh - 12px);
        max-height: calc(100vh - 12px);
        max-height: calc(100dvh - 12px);
        border-width: 6px;
        border-radius: 14px;

        .footer-bar {
            height: 116px;
            display: flex;
            flex-direction: column;
            align-items: stretch;
            justify-content: center;
            gap: 8px;
            padding: 10px 12px;
            box-sizing: border-box;

            button {
                width: 100%;
                min-height: 44px;
                margin: 0;
            }
        }
    }
`;

const containerStyle = css`
    display: flex;
    flex-direction: column;
    height: 100%;
    overflow: hidden;

    .header-wrapper {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 24px 32px 16px 32px;
        border-bottom: solid 1px ${color_shades_dark};
        flex-shrink: 0;

        h2 {
            margin: 0;
            font-size: 24px;
            font-weight: 600;
            color: ${color_shades_darkest};
        }

        .close-icon {
            cursor: pointer;
        }
    }

    .variations-container {
        flex: 1;
        overflow: hidden;
        padding: 16px 32px;

        .scrollable-content {
            padding-bottom: 16px;
        }

        .empty-state {
            display: flex;
            align-items: center;
            justify-content: center;
            height: 200px;
            color: ${color_shades_darker};
            font-style: italic;
            text-align: center;
        }

        .variation-item {
            padding: 16px;
            border: 2px solid ${color_shades_dark};
            border-radius: 8px;
            margin-bottom: 12px;
            cursor: pointer;
            transition: all 0.2s ease;
            background-color: white;

            &:hover {
                border-color: ${color_colors_ocean};
            }

            &.selected {
                border-color: ${color_colors_ocean};
                border-width: 3px;
                background-color: ${color_colors_light_ocean};
            }

            .variation-header {
                display: flex;
                justify-content: space-between;
                align-items: baseline;
                margin-bottom: 8px;
                gap: 16px;

                .variation-name {
                    font-weight: 600;
                    font-size: 16px;
                    color: ${color_shades_darkest};
                }

                .variation-full-name {
                    font-size: 11px;
                    font-family: monospace;
                    color: ${color_shades_darker};
                    flex-shrink: 0;
                }

                .variation-actions {
                    display: flex;
                    align-items: center;
                    gap: 12px;

                    .delete-button {
                        opacity: 0.6;
                        transition: opacity 0.2s ease;
                        margin: 0;

                        &:hover {
                            opacity: 1;
                        }

                        img {
                            width: 16px;
                            height: 16px;
                        }
                    }
                }
            }

            .variation-value {
                font-size: 14px;
                color: ${color_shades_darker};
                overflow: hidden;
                text-overflow: ellipsis;
                display: -webkit-box;
                -webkit-line-clamp: 3;
                -webkit-box-orient: vertical;
                line-height: 1.5;
                white-space: pre-wrap;
                word-break: break-word;
            }
        }
    }

    @media (max-width: 640px) {
        .header-wrapper {
            gap: 12px;
            padding: 14px 16px 12px 16px;

            h2 {
                min-width: 0;
                font-size: 18px;
                line-height: 1.2;
            }

            .close-icon {
                flex: 0 0 auto;
                width: 44px;
                min-width: 44px;
                height: 44px;
                margin: 0;
            }
        }

        .variations-container {
            padding: 12px 14px 124px 14px;

            .empty-state {
                height: 140px;
            }

            .variation-item {
                padding: 12px;
                margin-bottom: 10px;

                .variation-header {
                    align-items: center;
                    gap: 8px;

                    .variation-name {
                        min-width: 0;
                        overflow-wrap: anywhere;
                        font-size: 14px;
                    }

                    .variation-full-name {
                        display: none;
                    }

                    .variation-actions {
                        flex: 0 0 auto;
                    }
                }

                .variation-value {
                    font-size: 13px;
                }
            }
        }
    }
`;

export default ViewFieldVariationsModal;
